/**
 * Copilot 强制弹窗（落地页强引导）相关工具函数
 *
 * 触发语义（核心规则）：
 *   "1 天只强提醒 1 次" —— 三大页（经分/情报/舆情）合并视为一个整体，
 *   当日只要在其中任一页成功触发过一次强弹（无论后续用户是使用、关闭还是
 *   闲置自动关闭），后续访问任何三大页都不再触发；跨日（UTC 日期变更）
 *   或跨用户自动重置。
 *
 * 存储 key：__dba_force_copilot_state__
 * 存储 schema：
 *   {
 *     userId: string;
 *     date: 'YYYY-MM-DD';
 *     // 当日触发记录数组，保留具体页面便于排查；判断"是否再触发"只看 length
 *     triggeredPages: ('dashboard' | 'intelligence' | 'opinion')[];
 *     userClosed: boolean; // 用户主动关闭(X)后置 true，与 triggeredPages 共同决定不再弹
 *   }
 */

const FORCE_COPILOT_STATE_KEY = '__dba_force_copilot_state__';

export type TForceCopilotPage = 'dashboard' | 'intelligence' | 'opinion';

export interface IForceCopilotState {
  userId: string;
  date: string;
  triggeredPages: TForceCopilotPage[];
  userClosed: boolean;
}

/** 当前 UTC 日期字符串（YYYY-MM-DD），与现有 bubbleHelpers 保持一致 */
const getToday = (): string => {
  return new Date().toISOString().split('T')[0];
};

/**
 * 根据 pathname 解析当前所在的强引导页面；非三大页返回 null
 */
export const resolveForceCopilotPage = (
  pathname: string,
): TForceCopilotPage | null => {
  const lower = (pathname || '').toLowerCase();
  if (lower.startsWith('/dashboard')) return 'dashboard';
  if (lower.startsWith('/intelligence')) return 'intelligence';
  if (lower.startsWith('/opinion')) return 'opinion';
  return null;
};

/**
 * 读取存储；若日期/用户不匹配则视为无效返回 null（同时清掉脏数据）
 */
export const readForceCopilotState = (
  userId: string,
): IForceCopilotState | null => {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(FORCE_COPILOT_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as IForceCopilotState;
    const today = getToday();
    if (
      !parsed
      || parsed.userId !== userId
      || parsed.date !== today
      || !Array.isArray(parsed.triggeredPages)
    ) {
      // 跨日 / 跨用户 → 视为无效（自动清理交给下一次写入覆盖）
      return null;
    }
    return parsed;
  } catch (error) {
    console.warn('[forceCopilotHelpers] read failed:', error);
    return null;
  }
};

/**
 * 写入存储（合并模式）：保证 userId / date 始终是当前值
 */
const writeForceCopilotState = (
  userId: string,
  patch: Partial<Pick<IForceCopilotState, 'triggeredPages' | 'userClosed'>>,
): void => {
  if (!userId) return;
  try {
    const today = getToday();
    const prev = readForceCopilotState(userId);
    const next: IForceCopilotState = {
      userId,
      date: today,
      triggeredPages: patch.triggeredPages ?? prev?.triggeredPages ?? [],
      userClosed: patch.userClosed ?? prev?.userClosed ?? false,
    };
    localStorage.setItem(FORCE_COPILOT_STATE_KEY, JSON.stringify(next));
  } catch (error) {
    console.warn('[forceCopilotHelpers] write failed:', error);
  }
};

/**
 * 是否应当触发强制弹窗：
 * 1) 当前在三大页之一
 * 2) 当日该用户未主动关闭过（点 X）
 * 3) 当日尚未在任何一个三大页触发过强弹（"1 天只强提醒 1 次"，三大页合并计数）
 */
export const shouldTriggerForcePopup = (
  userId: string,
  page: TForceCopilotPage,
): boolean => {
  if (!userId || !page) return false;
  const state = readForceCopilotState(userId);
  if (!state) return true; // 当日全新（或跨日重置）
  if (state.userClosed) return false;
  // 三大页合并：当日只要触发过任一页，后续都不再触发
  if (state.triggeredPages.length > 0) return false;
  return true;
};

/**
 * 标记某页面在当日已触发过强弹（无论用户是否操作）
 */
export const markPageTriggered = (
  userId: string,
  page: TForceCopilotPage,
): void => {
  if (!userId || !page) return;
  const prev = readForceCopilotState(userId);
  const triggered = new Set(prev?.triggeredPages ?? []);
  triggered.add(page);
  writeForceCopilotState(userId, {
    triggeredPages: Array.from(triggered) as TForceCopilotPage[],
  });
};

/**
 * 用户主动关闭（点击 Copilot 头部 X）：当日全局不再弹
 */
export const markUserClosed = (userId: string): void => {
  if (!userId) return;
  writeForceCopilotState(userId, { userClosed: true });
};

/**
 * 是否已在当日触发过任意一次强弹（用于和 greeting 气泡互斥判断）
 */
export const hasAnyForcePopupTriggeredToday = (userId: string): boolean => {
  const state = readForceCopilotState(userId);
  if (!state) return false;
  return state.triggeredPages.length > 0 || state.userClosed;
};
